import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { AppRestEndPoint } from 'src/app/app-restEndPoint';
import { ApiService } from 'src/app/services/api.service';
import { PusherService } from 'src/app/services/pusher.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

/**
 * Which screen the modal is showing.
 *
 * The Yodlee FastLink iframe is mounted ONLY in 'linking'. Every other state unmounts
 * it, so whatever that frame happens to be displaying can never be the last thing the
 * client is left looking at. That matters here specifically: FastLink ends the flow by
 * POSTing back to the API host inside the iframe, and the API answers with
 * `X-Frame-Options: SAMEORIGIN`, so the browser renders its own "refused to connect"
 * page there — which clients were reading as "my linking failed" even though the
 * accounts had in fact been saved.
 */
export type LinkAccountView = 'linking' | 'verifying' | 'success' | 'cancelled' | 'failed' | 'apiError';

/**
 * Events the Rails Yodlee callback broadcasts on the household channel
 * (Apiclient::V1::YodleeController#yodlee_callback -> Comet.send -> Pusher).
 *
 *   LINK_SAVED     - emitted per provider account that came back with status 2, i.e.
 *                    Rails has saved a Bank row. This is the server telling us the
 *                    link is real.
 *   LINK_CANCELLED - emitted when any provider account came back with status 1, i.e.
 *                    the client backed out. Rails saves nothing in that branch.
 *   BANKS_UPDATED  - emitted once at the end of a successful callback, after the
 *                    refresh jobs are queued. Used as "the flow is over" marker.
 */
const LINK_SAVED_EVENT = 'yodlee_callback_initiated';
const LINK_CANCELLED_EVENT = 'yodlee_popup_cancelled';
const BANKS_UPDATED_EVENT = 'update_banks';

@Component({
  standalone: false,
	selector: 'app-link-account-modal',
	templateUrl: './link-account-modal.component.html',
	styleUrls: ['./link-account-modal.component.scss']
})

export class LinkAccountModalComponent implements OnInit, OnDestroy {
	currentUser: any;
	editLinkBank: any;
	editForm: any;
	fastLinkToken: any;
	rsessionToken: any;
	fastlinkUrl: any;
	callBackUrl: any;
	public bank_fastLinkToken: any;
	public bank_rsessionToken: any;
	public bank_users: any;
	public width;
	public height;
	access_token: any;
	showYodleeLoader: any = true;
	errorMessage: string = '';
	@Input() bank;
	@Input() type;
	@Input() selectedHousehold;

	/** Current screen. See LinkAccountView. */
	view: LinkAccountView = 'linking';
	/** Institution names we confirmed as newly linked, for the success screen. */
	linkedInstitutions: string[] = [];
	/**
	 * Bank rows that appeared while this modal was open. Tracked separately from the
	 * names because the Rails callback saves the Bank with only item_id/household_id —
	 * description and financial_institution are filled in later by the refresh job, so
	 * right after linking there is a real new connection but no name to show yet.
	 */
	newlyLinkedCount: number = 0;
	/** True when the confirmation call itself failed, so the success/failure wording can say so. */
	verifyUnavailable: boolean = false;

	/** Bank ids the household already had when this modal opened. */
	private knownBankIds: Set<number> = new Set<number>();
	/** False if the baseline call failed — without it a bank diff would be meaningless. */
	private baselineCaptured: boolean = false;
	/** Set when Rails confirms over Pusher that it saved at least one linked account. */
	private serverConfirmedLink: boolean = false;
	/** Set when Rails reports the client cancelled inside FastLink. */
	private cancelledInFastLink: boolean = false;
	/** Guards finishFlow so the first signal to arrive wins. */
	private flowFinished: boolean = false;
	/** True when the client asked to close — suppresses an outcome screen if there is nothing to report. */
	private dismissRequested: boolean = false;
	/** Household id for the Pusher channel, resolved without mutating the @Input. */
	private householdId: number = null;

	@ViewChild('yodleeForm') yodleeFormElement;
	constructor(
		private apiService: ApiService,
		private pusherService: PusherService,
		private sanitizer: DomSanitizer,
		public modalReference: NgbActiveModal

	) { }

	ngOnInit(): void {
		this.access_token = sessionStorage.getItem('token');
		this.householdId = this.resolveHouseholdId(this.selectedHousehold);

		// Listen for the outcome in EVERY flow. This used to be wired up only for
		// edit/refresh, so a brand-new link had no signal at all once the callback
		// iframe stopped rendering.
		this.bindLinkOutcome();
		this.captureBankBaseline();
		window.addEventListener('message', this.onWindowMessage, false);

		if (this.bank) {
			if (this.type && this.type == 'refresh') {
				this.linkAccountLoad('refresh', this.bank);
			} else {
				this.linkAccountLoad('edit', this.bank);
			}
		} else {
			this.linkAccountLoad('add');
		}
	}

	ngOnDestroy(): void {
		window.removeEventListener('message', this.onWindowMessage);
		// Unbind only the outcome events this modal owns. The channel subscription and
		// the dashboard's own progress handlers (refresh_worker_*, yodlee_* progress)
		// are deliberately left alone — the dashboard keeps using them to report the
		// background sync after this modal closes.
		this.pusherService.unbind(LINK_SAVED_EVENT);
		this.pusherService.unbind(LINK_CANCELLED_EVENT);
		this.pusherService.unbind(BANKS_UPDATED_EVENT);
	}

	/**
	 * Subscribe to the household channel and record what the server says about the
	 * link. Pusher is the load-bearing signal: it reaches us whether or not the
	 * browser was willing to render the callback response inside the iframe.
	 */
	private bindLinkOutcome(): void {
		// PusherService.bind() dereferences its client unguarded, so check it here rather
		// than crashing the whole modal when Pusher never initialised (missing env keys).
		if (this.householdId == null || !this.pusherService.pusher()) {
			// No channel to listen on. Close still verifies, so the client is never left
			// without an answer.
			return;
		}
		const self = this;
		this.pusherService.subscribeBankRefresh(this.householdId);
		this.pusherService.bind(LINK_SAVED_EVENT, function () {
			self.serverConfirmedLink = true;
		});
		this.pusherService.bind(LINK_CANCELLED_EVENT, function () {
			self.cancelledInFastLink = true;
			self.finishFlow();
		});
		this.pusherService.bind(BANKS_UPDATED_EVENT, function () {
			self.finishFlow();
		});
	}

	/**
	 * Fast path: the Rails callback view posts "refresh" to its parent when FastLink
	 * hands control back. It only arrives if the API host lets the client portal frame
	 * that response, which today it does not — so this is a bonus signal, not the one
	 * the fix relies on.
	 *
	 * Declared as a property so removeEventListener gets the same reference. The
	 * previous listener lived in the constructor, fired on ANY message from ANY origin,
	 * closed the modal, and was never removed — a stray postMessage from any embedded
	 * third party could tear the flow down.
	 */
	private onWindowMessage = (event: MessageEvent): void => {
		if (!this.isApiOrigin(event.origin)) {
			return;
		}
		if (event.data !== 'refresh') {
			return;
		}
		// Deliberately does not set serverConfirmedLink: this message is posted for a
		// cancel too, so it means "the flow ended", not "something was linked".
		this.finishFlow();
	};

	private isApiOrigin(origin: string): boolean {
		if (!origin) {
			return false;
		}
		try {
			return origin === new URL(AppRestEndPoint.CURRENT_API_URL).origin;
		} catch (e) {
			return false;
		}
	}

	private resolveHouseholdId(household: any): number {
		if (household === null || household === undefined) {
			return null;
		}
		const raw = typeof household === 'object' ? household.id : household;
		const id = Number(raw);
		return isNaN(id) || id <= 0 ? null : id;
	}

	/**
	 * Snapshot the household's banks before FastLink runs. Comparing this against the
	 * same call afterwards is how we answer "were the accounts actually linked" from
	 * the API rather than from a UI guess.
	 */
	private captureBankBaseline(): void {
		this.apiService.getCall(AppRestEndPoint.ASSET.GET_ACC_DETAILS, '').subscribe(response => {
			this.knownBankIds = new Set<number>(this.banksFrom(response).map(bank => Number(bank.id)));
			this.baselineCaptured = true;
		}, () => {
			// Without a baseline every existing bank would look new, which would report a
			// false success with an invented count. Verification falls back to the
			// server's own confirmation instead.
			this.baselineCaptured = false;
		});
	}

	/**
	 * The flow is over. Tear down the FastLink frame and go confirm the result with the
	 * API. First caller wins, so a Pusher event and a postMessage arriving together
	 * only verify once.
	 */
	private finishFlow(): void {
		if (this.flowFinished) {
			return;
		}
		this.flowFinished = true;
		this.showYodleeLoader = false;
		this.view = 'verifying';
		this.verifyLinkedAccounts();
	}

	/**
	 * Ground truth: re-read the household's banks and diff against the baseline. A new
	 * bank row means the Rails callback ran and saved the provider account.
	 */
	private verifyLinkedAccounts(): void {
		this.apiService.getCall(AppRestEndPoint.ASSET.GET_ACC_DETAILS, '').subscribe(response => {
			const newBanks = this.baselineCaptured
				? this.banksFrom(response).filter(bank => !this.knownBankIds.has(Number(bank.id)))
				: [];
			this.newlyLinkedCount = newBanks.length;
			this.linkedInstitutions = newBanks.map(bank => this.institutionName(bank)).filter(name => !!name);
			this.settleOutcome(newBanks.length > 0);
		}, () => {
			// Could not reach the API to confirm. Report what the server already told us
			// over Pusher rather than guessing from the UI.
			this.verifyUnavailable = true;
			this.newlyLinkedCount = 0;
			this.linkedInstitutions = [];
			this.settleOutcome(false);
		});
	}

	private settleOutcome(sawNewBank: boolean): void {
		const outcome = this.outcomeFor(sawNewBank);
		// A client who just dismissed the dialog without linking anything does not need
		// a failure screen — there is nothing to report. Close instead. A dismissal that
		// turns out to have linked something still gets told, which is the whole point.
		if (outcome !== 'success' && this.dismissRequested) {
			this.finishAndClose();
			return;
		}
		this.view = outcome;
	}

	private outcomeFor(sawNewBank: boolean): LinkAccountView {
		// A relink (edit/refresh) reuses the existing Bank row, so "a new bank appeared"
		// is not the test in that flow — the server's confirmation is.
		if (sawNewBank || this.serverConfirmedLink) {
			return 'success';
		}
		if (this.cancelledInFastLink) {
			return 'cancelled';
		}
		return 'failed';
	}

	/**
	 * apiclient/v1/banks renders `banks` as an index-keyed OBJECT ({"0": {...}}), not an
	 * array — see banks/index.json.jbuilder. Handle both shapes, and drop rows with no
	 * id since they cannot take part in the diff.
	 */
	private banksFrom(response: any): any[] {
		const raw = response && response.banks ? response.banks : null;
		if (!raw) {
			return [];
		}
		const list = Array.isArray(raw) ? raw : Object.keys(raw).map(key => raw[key]);
		return list.filter(bank => bank && bank.id !== null && bank.id !== undefined);
	}

	private institutionName(bank: any): string {
		if (bank && bank.financial_institution && bank.financial_institution.description) {
			return bank.financial_institution.description;
		}
		return '';
	}

	linkAccountLoad(type, bank: any = {}, form = '') {
		if (type === 'add') {
			this.getToken(false, type);
		} else if (type === 'edit' || type == 'refresh') {
			this.editLinkBank = bank.bank;
			this.getToken(true, type);
		}
	}

	getToken(flag, type) {
		//this.presentLoading();
		this.showYodleeLoader = true;
		if (!(this.fastLinkToken) || type === 'timeout') {
			this.apiService.getCall(AppRestEndPoint.ASSET.GET_TOKEN, '').subscribe(data => {
				this.showYodleeLoader = false;
				this.fastLinkToken = data.access_token;
				this.rsessionToken = data.user_session_token;
				this.fastlinkUrl = this.sanitizer.bypassSecurityTrustResourceUrl(data.fastlink_url);

				// for fix bank per user
				this.bank_fastLinkToken = this.fastLinkToken;
				this.bank_rsessionToken = this.rsessionToken;
				this.bank_users = data.users;

				if (flag === true) {
					if (type && (type === 'edit' || type == 'refresh')) {
						this.setEditUrl(this.editLinkBank, type);
					}
				} else if (flag === false) {
					this.setLinkUrl(true);
				}
			}, (err) => {
				this.showYodleeLoader = false;
				this.handleApiError(err);
			});
		} else {
			if (flag === true) {
				if (type && (type === 'edit' || type == 'refresh')) {
					this.setEditUrl(this.editLinkBank, type);
				}
			} else if (flag === false) {
				this.setLinkUrl(true);
			}
		}
	}

	handleApiError(error: any) {
		// Hide the iframe and show error message
		this.fastlinkUrl = null;
		this.view = 'apiError';
	}

	setLinkUrl(flag) {
		this.setFastlinkForm('');
		this.callBackUrl = 'callback=' + AppRestEndPoint.CURRENT_ENV + '/yodlee/yodlee_callback?access_token=' + this.access_token + '&type=callback';
		if (flag === true) {
			setTimeout(_ => this.submitFastlinkForm());
		}
	}

	// sets fastlink tokens based on a bank, or sets them to default
	setFastlinkForm(bank) {
		let bank_user;

		if (bank && this.bank_users) {
			bank_user = this.bank_users.find(function (user) {
				if (user.user_id === bank.user_id) {
					return user.fastlink_data;
				}
			});
		}

		if (bank_user) {
			this.fastLinkToken = bank_user.fastlink_data.access_token;
			this.rsessionToken = bank_user.fastlink_data.user_session_token;
		} else {
			this.fastLinkToken = this.bank_fastLinkToken;
			this.rsessionToken = this.bank_rsessionToken;
		}
	}

	setEditUrl(bank, type = '') {
		if (!bank) {
			return;
		}
		this.setFastlinkForm(bank);
		this.callBackUrl = 'callback=' + AppRestEndPoint.CURRENT_ENV +
			'/yodlee/yodlee_callback?access_token=' + this.access_token + '&type=callback&flow=edit&siteAccountId=' + bank.item_id;
		setTimeout(_ => this.submitFastlinkForm());
	}

	/**
	 * The form only exists while the FastLink screen is mounted. A cancel signal can
	 * land before this deferred submit runs, so guard rather than throwing on a
	 * detached ViewChild.
	 */
	private submitFastlinkForm(): void {
		if (this.yodleeFormElement && this.yodleeFormElement.nativeElement) {
			this.yodleeFormElement.nativeElement.submit();
		}
	}

	/** Restart the flow from the outcome screen with a fresh FastLink token. */
	retryLinking(): void {
		this.flowFinished = false;
		this.dismissRequested = false;
		this.serverConfirmedLink = false;
		this.cancelledInFastLink = false;
		this.verifyUnavailable = false;
		this.newlyLinkedCount = 0;
		this.linkedInstitutions = [];
		this.view = 'linking';
		// Re-baseline: the previous attempt may have changed the bank list.
		this.captureBankBaseline();
		// Force a new token — the one from the last attempt is spent.
		this.fastLinkToken = null;
		if (this.bank) {
			this.linkAccountLoad(this.type === 'refresh' ? 'refresh' : 'edit', this.bank);
		} else {
			this.linkAccountLoad('add');
		}
	}

	/**
	 * Close from the header or an outcome screen.
	 *
	 * While FastLink is still up this does NOT close blind — it verifies first, so a
	 * client who finished linking but was left looking at a dead frame is still told it
	 * worked. Once an outcome is on screen, it closes for real.
	 */
	closeModal(data?: string) {
		if (this.view === 'linking' && !this.flowFinished) {
			this.dismissRequested = true;
			this.finishFlow();
			return;
		}
		this.finishAndClose();
	}

	private finishAndClose(): void {
		// reload keeps the dashboard's existing behaviour: refetch net worth and bind the
		// Pusher handlers that report the background sync.
		this.modalReference.close({
			success: 'closeModal',
			reload: this.view === 'success' || this.serverConfirmedLink,
			edit: false
		});
	}
}
